import { ReactNode, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Footer } from "@/components/Footer";
import { SEOMetaTags } from "@/components/SEOMetaTags";
import { StructuredData } from "@/components/StructuredData";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, XCircle, Info, AlertTriangle } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface AuthWrapperProps {
  children: ReactNode;
}

export default function AuthWrapper({ children }: AuthWrapperProps) {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [authView, setAuthView] = useState<'sign_in' | 'sign_up'>('sign_up'); // Default to sign up for new users
  const { toast } = useToast();
  
  // Password strength state
  const [password, setPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState<string[]>([]);
  
  // Email validation state
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  
  // Terms acceptance state
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Rate limiting state
  const [authAttempts, setAuthAttempts] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitExpiry, setRateLimitExpiry] = useState<number | null>(null);
  
  const MAX_AUTH_ATTEMPTS = 5;
  const RATE_LIMIT_WINDOW_MS = 15 * 60 * 1000; // 15 minutes
  const LOCKOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes

  // Check and update rate limit status
  useEffect(() => {
    const checkRateLimit = () => {
      const now = Date.now();
      
      // Check if lockout has expired
      if (rateLimitExpiry && now > rateLimitExpiry) {
        setIsRateLimited(false);
        setAuthAttempts(0);
        setRateLimitExpiry(null);
        localStorage.removeItem('auth_attempts');
        localStorage.removeItem('auth_lockout_expiry');
        return;
      }
      
      // Load from localStorage on mount
      const storedAttempts = localStorage.getItem('auth_attempts');
      const storedExpiry = localStorage.getItem('auth_lockout_expiry');
      
      if (storedAttempts) {
        const attempts = parseInt(storedAttempts, 10);
        setAuthAttempts(attempts);
        
        if (attempts >= MAX_AUTH_ATTEMPTS && storedExpiry) {
          const expiry = parseInt(storedExpiry, 10);
          if (now < expiry) {
            setIsRateLimited(true);
            setRateLimitExpiry(expiry);
          }
        }
      }
    };
    
    checkRateLimit();
    const interval = setInterval(checkRateLimit, 1000); // Check every second
    
    return () => clearInterval(interval);
  }, [rateLimitExpiry]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    }).catch((error) => {
      console.error("Auth session error:", error);
      setLoading(false);
      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: "Failed to load session. Please refresh the page.",
      });
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      
      // Handle auth events with user feedback
      if (event === 'SIGNED_IN') {
        // Reset rate limiting on successful login
        setAuthAttempts(0);
        setIsRateLimited(false);
        setRateLimitExpiry(null);
        localStorage.removeItem('auth_attempts');
        localStorage.removeItem('auth_lockout_expiry');
        
        // Check for previous page redirect
        const returnTo = sessionStorage.getItem('returnTo');
        if (returnTo && returnTo !== '/') {
          sessionStorage.removeItem('returnTo');
          // Defer redirect to avoid race conditions
          setTimeout(() => {
            window.location.href = returnTo;
          }, 100);
        }
        
        // Differentiate between new signup and returning login
        // Check if user was created within last 10 seconds OR if we're on signup view
        const isSignup = (session?.user?.created_at && 
          new Date(session.user.created_at).getTime() > Date.now() - 10000) ||
          authView === 'sign_up';
        
        if (isSignup) {
          toast({
            title: "Welcome to Scope Manager!",
            description: "Your account has been created successfully. Let's get started!",
          });
        } else {
          toast({
            title: "Welcome back!",
            description: "You've successfully signed in.",
          });
        }
      } else if (event === 'SIGNED_OUT') {
        // Detect if this was user-initiated or session expiry
        const wasUserInitiated = sessionStorage.getItem('user_logout') === 'true';
        
        if (!wasUserInitiated) {
          // Session expired - store current location for redirect after re-login
          const currentPath = window.location.pathname;
          if (currentPath !== '/' && !currentPath.startsWith('/auth')) {
            sessionStorage.setItem('returnTo', currentPath);
          }
          
          toast({
            variant: "destructive",
            title: "Session Expired",
            description: "Your session has expired. Please log in again.",
            duration: 5000,
          });
        } else {
          // User-initiated logout
          sessionStorage.removeItem('user_logout');
          toast({
            title: "Signed out",
            description: "You've been signed out successfully.",
          });
        }
      } else if (event === 'TOKEN_REFRESHED') {
        // Log token refresh for monitoring
        console.log('Session token refreshed successfully');
      } else if (event === 'USER_UPDATED') {
        toast({
          title: "Profile updated",
          description: "Your profile has been updated.",
        });
      } else if (event === 'PASSWORD_RECOVERY') {
        toast({
          title: "Password recovery",
          description: "Check your email for password reset instructions.",
        });
      }
      
      // Track failed auth attempts
      if (event === 'SIGNED_IN') {
        // Success - reset counter
        setAuthAttempts(0);
        localStorage.removeItem('auth_attempts');
      }
    });

    return () => subscription.unsubscribe();
  }, [toast, authView]);
  
  // Password strength checker
  useEffect(() => {
    if (!password) {
      setPasswordStrength(0);
      setPasswordFeedback([]);
      return;
    }
    
    let strength = 0;
    const feedback: string[] = [];
    
    // Length check
    if (password.length >= 8) {
      strength += 20;
    } else {
      feedback.push("At least 8 characters");
    }
    
    if (password.length >= 12) {
      strength += 10;
    }
    
    // Lowercase and uppercase
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) {
      strength += 20;
    } else {
      feedback.push("Mix of uppercase and lowercase");
    }
    
    // Numbers
    if (/\d/.test(password)) {
      strength += 20;
    } else {
      feedback.push("Include numbers");
    }
    
    // Special characters
    if (/[^a-zA-Z0-9]/.test(password)) {
      strength += 20;
    } else {
      feedback.push("Add special characters (!@#$%)");
    }
    
    // No common patterns
    if (!/(.)\1{2,}/.test(password) && !/123|abc|password|qwerty/i.test(password)) {
      strength += 10;
    } else {
      feedback.push("Avoid common patterns");
    }
    
    setPasswordStrength(strength);
    setPasswordFeedback(feedback);
  }, [password]);
  
  // Email validation with debounce
  useEffect(() => {
    if (!email) {
      setEmailError(null);
      return;
    }
    
    const timer = setTimeout(() => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      
      if (!emailRegex.test(email)) {
        setEmailError("Invalid email format");
        return;
      }
      
      // Common typo detection
      const commonTypos: { [key: string]: string } = {
        'gmial': 'gmail',
        'gmai': 'gmail',
        'gmil': 'gmail',
        'yahooo': 'yahoo',
        'yaho': 'yahoo',
        'hotmial': 'hotmail',
        'outlok': 'outlook',
      };
      
      const domain = email.split('@')[1];
      const domainBase = domain?.split('.')[0];
      
      if (domainBase && commonTypos[domainBase.toLowerCase()]) {
        setEmailError(`Did you mean ${commonTypos[domainBase.toLowerCase()]}.com?`);
        return;
      }
      
      setEmailError(null);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [email]);
  
  // Track auth errors and implement rate limiting
  useEffect(() => {
    const handleAuthError = () => {
      const newAttempts = authAttempts + 1;
      setAuthAttempts(newAttempts);
      localStorage.setItem('auth_attempts', newAttempts.toString());
      
      if (newAttempts >= MAX_AUTH_ATTEMPTS) {
        const expiry = Date.now() + LOCKOUT_DURATION_MS;
        setIsRateLimited(true);
        setRateLimitExpiry(expiry);
        localStorage.setItem('auth_lockout_expiry', expiry.toString());
        
        toast({
          variant: "destructive",
          title: "Too Many Attempts",
          description: `Account locked for 15 minutes due to multiple failed login attempts. Please try again later.`,
          duration: 10000,
        });
      } else if (newAttempts >= 3) {
        toast({
          variant: "destructive",
          title: "Authentication Failed",
          description: `Invalid credentials. ${MAX_AUTH_ATTEMPTS - newAttempts} attempts remaining before temporary lockout.`,
          duration: 5000,
        });
      }
    };
    
    // Listen for auth errors from Supabase
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        // Don't count password recovery as failed attempt
        return;
      }
    });
    
    return () => subscription.unsubscribe();
  }, [authAttempts, toast]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" role="status" aria-live="polite">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" aria-hidden="true"></div>
        <span className="sr-only">Loading authentication...</span>
      </div>
    );
  }

  if (!session) {
    return (
      <>
        {/* SEO Meta Tags for Landing Page */}
        <SEOMetaTags
          title="Scope Manager | AI-Powered Project Management for Steel Fabrication"
          description="Sign in to Scope Manager - Upload specs, search with AI, and collaborate on technical documentation. Purpose-built for steel fabrication, misc metals, and metal panel companies."
          keywords={[
            'steel fabrication login',
            'project management software',
            'structural steel',
            'AISC specifications',
            'NOMMA standards',
            'metal panel documentation',
            'fabrication shop software',
            'construction document management'
          ]}
        />
        
        {/* Structured Data for SEO */}
        <StructuredData type="all" />
        
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-muted/30 to-background">
          <div className="flex-1 flex items-center justify-center p-4 py-12">
            <main role="main" aria-label="Authentication" className="w-full max-w-4xl">
              {/* Hero Section */}
              <div className="text-center mb-12 px-4" role="region" aria-label="Product overview">
                <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Scope Manager
                </h1>
                <p className="text-xl md:text-2xl text-foreground font-medium mb-6">
                  AI-Powered Document Management for Steel Fabrication
                </p>
                <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto mb-8">
                  Upload specs, search with AI, and collaborate on technical documentation. 
                  Purpose-built for structural steel, misc metals, and metal panel companies.
                </p>
                
                {/* Features Grid */}
                <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto mb-12">
                  <div className="flex flex-col items-center text-center p-4 rounded-lg bg-muted/50" role="listitem">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3" aria-hidden="true">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">Smart Document Search</h3>
                    <p className="text-sm text-muted-foreground">Find tolerances, specs, and details instantly with AI-powered search</p>
                  </div>
                  
                  <div className="flex flex-col items-center text-center p-4 rounded-lg bg-muted/50" role="listitem">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3" aria-hidden="true">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">Team Collaboration</h3>
                    <p className="text-sm text-muted-foreground">Share conversations and documents across your entire project team</p>
                  </div>
                  
                  <div className="flex flex-col items-center text-center p-4 rounded-lg bg-muted/50" role="listitem">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3" aria-hidden="true">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">Industry Standards</h3>
                    <p className="text-sm text-muted-foreground">Built for AISC, NOMMA, and metal fabrication specifications</p>
                  </div>
                </div>
              </div>
              
              {/* Auth Card */}
            <Card className="w-full max-w-md mx-auto border-primary/20 shadow-xl" role="region" aria-label="Sign in form">
              <CardHeader className="text-center space-y-2">
                <CardTitle className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  SCOPE MANAGER
                </CardTitle>
                <CardDescription>
                  AI-powered document management for steel fabrication
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={authView} onValueChange={(value) => setAuthView(value as 'sign_in' | 'sign_up')} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6" role="tablist" aria-label="Authentication type">
                    <TabsTrigger 
                      value="sign_up"
                      className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                      role="tab"
                      aria-selected={authView === 'sign_up'}
                      aria-controls="signup-panel"
                    >
                      Sign Up
                    </TabsTrigger>
                    <TabsTrigger 
                      value="sign_in"
                      className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                      role="tab"
                      aria-selected={authView === 'sign_in'}
                      aria-controls="signin-panel"
                    >
                      Login
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="sign_up" className="mt-0" id="signup-panel" role="tabpanel" aria-labelledby="sign_up-tab">
                    <div className="mb-4 space-y-3">
                      <p className="text-sm text-center text-muted-foreground">
                        Create your account to get started
                      </p>
                      
                      {/* Email Validation Feedback */}
                      {email && emailError && (
                        <Alert variant="destructive" className="bg-destructive/10 border-destructive/50" role="alert">
                          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                          <AlertDescription className="text-xs">
                            {emailError}
                          </AlertDescription>
                        </Alert>
                      )}
                      
                      {/* Password Strength Indicator */}
                      {password && (
                        <Alert 
                          className={`border ${
                            passwordStrength >= 80 ? 'bg-green-500/10 border-green-500/50' :
                            passwordStrength >= 60 ? 'bg-yellow-500/10 border-yellow-500/50' :
                            'bg-red-500/10 border-red-500/50'
                          }`}
                          role="status"
                        >
                          <Info className="h-4 w-4" aria-hidden="true" />
                          <AlertDescription className="text-xs space-y-2">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium">
                                Password strength: {
                                  passwordStrength >= 80 ? 'Strong' :
                                  passwordStrength >= 60 ? 'Medium' :
                                  'Weak'
                                }
                              </span>
                              <span className="text-muted-foreground">{passwordStrength}%</span>
                            </div>
                            <Progress 
                              value={passwordStrength} 
                              className={`h-2 ${
                                passwordStrength >= 80 ? '[&>div]:bg-green-500' :
                                passwordStrength >= 60 ? '[&>div]:bg-yellow-500' :
                                '[&>div]:bg-red-500'
                              }`}
                              aria-label={`Password strength: ${passwordStrength}%`}
                            />
                            {passwordFeedback.length > 0 && (
                              <ul className="list-none space-y-1 mt-2" role="list">
                                {passwordFeedback.map((feedback, index) => (
                                  <li key={index} className="flex items-center gap-2 text-muted-foreground" role="listitem">
                                    <XCircle className="h-3 w-3" aria-hidden="true" />
                                    <span>{feedback}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </AlertDescription>
                        </Alert>
                      )}
                      
                      {/* Rate Limit Warning */}
                      {isRateLimited && rateLimitExpiry && (
                        <Alert variant="destructive" className="border-destructive" role="alert" aria-live="assertive">
                          <XCircle className="h-4 w-4" aria-hidden="true" />
                          <AlertDescription className="text-xs">
                            <p className="font-medium">Account temporarily locked</p>
                            <p className="mt-1" role="timer" aria-live="polite" aria-atomic="true">
                              Too many failed attempts. Please wait {Math.ceil((rateLimitExpiry - Date.now()) / 60000)} minutes before trying again.
                            </p>
                          </AlertDescription>
                        </Alert>
                      )}
                      
                      {authAttempts >= 3 && authAttempts < MAX_AUTH_ATTEMPTS && !isRateLimited && (
                        <Alert variant="destructive" className="bg-destructive/10 border-destructive/50" role="alert" aria-live="polite">
                          <Info className="h-4 w-4" aria-hidden="true" />
                          <AlertDescription className="text-xs">
                            Warning: {MAX_AUTH_ATTEMPTS - authAttempts} attempts remaining before temporary lockout
                          </AlertDescription>
                        </Alert>
                      )}
                      
                      {/* Password Requirements */}
                      <Alert className="bg-muted/50 border-border" role="status">
                        <Info className="h-4 w-4" aria-hidden="true" />
                        <AlertDescription className="text-xs space-y-1">
                          <p className="font-medium">Password requirements:</p>
                          <ul className="list-none space-y-1 mt-2" role="list">
                            <li className="flex items-center gap-2" role="listitem">
                              <CheckCircle2 className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                              <span>At least 6 characters</span>
                            </li>
                            <li className="flex items-center gap-2" role="listitem">
                              <CheckCircle2 className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                              <span>Valid email address</span>
                            </li>
                          </ul>
                        </AlertDescription>
                      </Alert>
                    </div>
                    
                    <div 
                      className={isRateLimited ? "opacity-50 pointer-events-none" : ""}
                      aria-disabled={isRateLimited}
                      aria-describedby={isRateLimited ? "rate-limit-message" : undefined}
                    >
                      {/* Email input field */}
                      <div className="mb-4">
                        <label htmlFor="signup-email" className="block text-sm font-medium mb-2">
                          Email address
                        </label>
                        <input
                          id="signup-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@company.com"
                          className="w-full px-3 py-2 border rounded-md bg-background text-foreground"
                          disabled={isRateLimited}
                        />
                      </div>
                      
                      {/* Password input field */}
                      <div className="mb-4">
                        <label htmlFor="signup-password" className="block text-sm font-medium mb-2">
                          Password (min. 8 characters)
                        </label>
                        <input
                          id="signup-password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter a strong password"
                          className="w-full px-3 py-2 border rounded-md bg-background text-foreground"
                          disabled={isRateLimited}
                        />
                      </div>
                      
                      {/* Terms of Service & Privacy Policy Acceptance */}
                      <div className="mb-6">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="accept-terms"
                            checked={acceptedTerms}
                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                            disabled={isRateLimited}
                            className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                            aria-describedby="terms-description"
                            required
                          />
                          <label 
                            htmlFor="accept-terms" 
                            className="text-sm text-muted-foreground cursor-pointer select-none"
                            id="terms-description"
                          >
                            I agree to the{' '}
                            <a 
                              href="/terms-of-service" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary hover:underline font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Terms of Service
                            </a>
                            {' '}and{' '}
                            <a 
                              href="/privacy-policy" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary hover:underline font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Privacy Policy
                            </a>
                          </label>
                        </div>
                        {!acceptedTerms && password && email && (
                          <p className="text-xs text-destructive mt-2 ml-7" role="alert">
                            You must accept the Terms of Service and Privacy Policy to create an account
                          </p>
                        )}
                      </div>
                      
                      {/* Submit Button - Only enable if terms accepted */}
                      <button
                        type="button"
                        onClick={async () => {
                          if (!acceptedTerms) {
                            toast({
                              variant: "destructive",
                              title: "Terms Required",
                              description: "Please accept the Terms of Service and Privacy Policy to continue.",
                            });
                            return;
                          }
                          
                          if (!email || !password) {
                            toast({
                              variant: "destructive",
                              title: "Missing Information",
                              description: "Please enter both email and password.",
                            });
                            return;
                          }
                          
                          if (emailError) {
                            toast({
                              variant: "destructive",
                              title: "Invalid Email",
                              description: emailError,
                            });
                            return;
                          }
                          
                          if (passwordStrength < 60) {
                            toast({
                              variant: "destructive",
                              title: "Weak Password",
                              description: "Please choose a stronger password.",
                            });
                            return;
                          }
                          
                          // Proceed with signup
                          const { error } = await supabase.auth.signUp({
                            email,
                            password,
                            options: {
                              emailRedirectTo: `${window.location.origin}/`,
                            },
                          });
                          
                          if (error) {
                            console.error("Signup error:", error);
                            
                            // Handle specific error cases
                            let errorTitle = "Signup Failed";
                            let errorDescription = error.message;
                            
                            if (error.message.includes("already registered") || error.message.includes("email_exists")) {
                              errorTitle = "Account Already Exists";
                              errorDescription = "An account with this email already exists. Please try logging in instead.";
                            } else if (error.message.includes("invalid email")) {
                              errorTitle = "Invalid Email";
                              errorDescription = "Please enter a valid email address.";
                            } else if (error.message.includes("password")) {
                              errorTitle = "Password Error";
                              errorDescription = "Password does not meet requirements. Please choose a stronger password.";
                            } else if (error.message.includes("rate limit")) {
                              errorTitle = "Too Many Attempts";
                              errorDescription = "Too many signup attempts. Please wait a moment and try again.";
                            }
                            
                            toast({
                              variant: "destructive",
                              title: errorTitle,
                              description: errorDescription,
                            });
                          } else {
                            // Success
                            toast({
                              title: "Account Created",
                              description: "Welcome to Scope Manager! You can now start using the app.",
                            });
                          }
                        }}
                        disabled={isRateLimited || !acceptedTerms || !email || !password || !!emailError}
                        className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${
                          isRateLimited || !acceptedTerms || !email || !password || !!emailError
                            ? 'bg-muted text-muted-foreground cursor-not-allowed'
                            : 'bg-primary text-primary-foreground hover:bg-primary/90'
                        }`}
                      >
                        {isRateLimited ? 'Account Locked' : 'Create Account'}
                      </button>
                      
                      {/* Google OAuth */}
                      <div className="mt-4">
                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-border" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                          </div>
                        </div>
                        
                        <button
                          type="button"
                          onClick={async () => {
                            if (!acceptedTerms) {
                              toast({
                                variant: "destructive",
                                title: "Terms Required",
                                description: "Please accept the Terms of Service and Privacy Policy to continue.",
                              });
                              return;
                            }
                            
                            const { error } = await supabase.auth.signInWithOAuth({
                              provider: 'google',
                              options: {
                                redirectTo: `${window.location.origin}/`,
                              },
                            });
                            
                            if (error) {
                              toast({
                                variant: "destructive",
                                title: "OAuth Failed",
                                description: error.message,
                              });
                            }
                          }}
                          disabled={isRateLimited || !acceptedTerms}
                          className={`w-full mt-4 py-2 px-4 border rounded-md font-medium transition-colors flex items-center justify-center gap-2 ${
                            isRateLimited || !acceptedTerms
                              ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                              : 'border-border bg-background text-foreground hover:bg-muted'
                          }`}
                        >
                          <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          Sign up with Google
                        </button>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="sign_in" className="mt-0" id="signin-panel" role="tabpanel" aria-labelledby="sign_in-tab">
                    <div className="mb-4 space-y-3">
                      <p className="text-sm text-center text-muted-foreground">
                        Welcome back! Sign in to continue
                      </p>
                      
                      {/* Rate Limit Warning */}
                      {isRateLimited && rateLimitExpiry && (
                        <Alert variant="destructive" className="border-destructive" role="alert" aria-live="assertive">
                          <XCircle className="h-4 w-4" aria-hidden="true" />
                          <AlertDescription className="text-xs">
                            <p className="font-medium">Account temporarily locked</p>
                            <p className="mt-1" role="timer" aria-live="polite" aria-atomic="true">
                              Too many failed attempts. Please wait {Math.ceil((rateLimitExpiry - Date.now()) / 60000)} minutes before trying again.
                            </p>
                          </AlertDescription>
                        </Alert>
                      )}
                      
                      {authAttempts >= 3 && authAttempts < MAX_AUTH_ATTEMPTS && !isRateLimited && (
                        <Alert variant="destructive" className="bg-destructive/10 border-destructive/50" role="alert" aria-live="polite">
                          <Info className="h-4 w-4" aria-hidden="true" />
                          <AlertDescription className="text-xs">
                            Warning: {MAX_AUTH_ATTEMPTS - authAttempts} attempts remaining before temporary lockout
                          </AlertDescription>
                        </Alert>
                      )}
                      
                      {/* Login Help */}
                      <Alert className="bg-muted/50 border-border" role="status">
                        <Info className="h-4 w-4" aria-hidden="true" />
                        <AlertDescription className="text-xs">
                          <p>Enter your email and password to access your account.</p>
                          <p className="mt-1 text-muted-foreground">Forgot your password? Contact your administrator.</p>
                        </AlertDescription>
                      </Alert>
                    </div>
                    
                    <div 
                      className={isRateLimited ? "opacity-50 pointer-events-none" : ""}
                      aria-disabled={isRateLimited}
                      aria-describedby={isRateLimited ? "rate-limit-message" : undefined}
                    >
                      {/* Custom Login Form */}
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        
                        if (isRateLimited) {
                          toast({
                            variant: "destructive",
                            title: "Account Locked",
                            description: `Too many failed attempts. Please wait ${Math.ceil((rateLimitExpiry! - Date.now()) / 60000)} minutes.`,
                          });
                          return;
                        }

                        const formData = new FormData(e.currentTarget);
                        const loginEmail = formData.get('email') as string;
                        const loginPassword = formData.get('password') as string;

                        if (!loginEmail || !loginPassword) {
                          toast({
                            variant: "destructive",
                            title: "Missing Information",
                            description: "Please enter both email and password.",
                          });
                          return;
                        }

                        try {
                          // Attempt login
                          const { data, error } = await supabase.auth.signInWithPassword({
                            email: loginEmail,
                            password: loginPassword,
                          });

                          if (error) {
                            // Track failed attempt
                            const newAttempts = authAttempts + 1;
                            setAuthAttempts(newAttempts);
                            localStorage.setItem('auth_attempts', newAttempts.toString());

                            // Log failed attempt to database
                            await supabase.functions.invoke('log-auth-event', {
                              body: {
                                event_type: 'login',
                                event_status: 'failure',
                                failure_reason: error.message,
                                metadata: {
                                  email: loginEmail,
                                  attempt_number: newAttempts
                                }
                              }
                            });

                            // Check if we should lock the account
                            if (newAttempts >= MAX_AUTH_ATTEMPTS) {
                              const expiry = Date.now() + LOCKOUT_DURATION_MS;
                              setIsRateLimited(true);
                              setRateLimitExpiry(expiry);
                              localStorage.setItem('auth_lockout_expiry', expiry.toString());

                              // Log rate limit event
                              await supabase.functions.invoke('log-auth-event', {
                                body: {
                                  event_type: 'login',
                                  event_status: 'rate_limited',
                                  failure_reason: 'Maximum login attempts exceeded',
                                  metadata: {
                                    email: loginEmail,
                                    lockout_duration_minutes: 15
                                  }
                                }
                              });

                              toast({
                                variant: "destructive",
                                title: "Account Locked",
                                description: "Too many failed login attempts. Your account has been locked for 15 minutes.",
                                duration: 10000,
                              });
                            } else if (newAttempts >= 3) {
                              toast({
                                variant: "destructive",
                                title: "Invalid credentials",
                                description: `${MAX_AUTH_ATTEMPTS - newAttempts} attempts remaining before lockout.`,
                                duration: 5000,
                              });
                            } else {
                              toast({
                                variant: "destructive",
                                title: "Invalid credentials",
                                description: "Please check your email and password.",
                              });
                            }

                            // DO NOT clear password field (per spec)
                            return;
                          }

                          // Success - log it
                          if (data.user) {
                            await supabase.functions.invoke('log-auth-event', {
                              body: {
                                user_id: data.user.id,
                                event_type: 'login',
                                event_status: 'success',
                                metadata: {
                                  email: loginEmail
                                }
                              }
                            });
                          }

                          // Reset rate limiting on successful login
                          setAuthAttempts(0);
                          setIsRateLimited(false);
                          setRateLimitExpiry(null);
                          localStorage.removeItem('auth_attempts');
                          localStorage.removeItem('auth_lockout_expiry');

                          // Success toast handled by onAuthStateChange
                          
                        } catch (err) {
                          console.error("Login error:", err);
                          toast({
                            variant: "destructive",
                            title: "Login Error",
                            description: "An unexpected error occurred. Please try again.",
                          });
                        }
                      }} className="space-y-4">
                        {/* Email Input */}
                        <div>
                          <label htmlFor="login-email" className="block text-sm font-medium mb-2">
                            Email address
                          </label>
                          <input
                            id="login-email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                            disabled={isRateLimited}
                            placeholder="you@company.com"
                            className="w-full px-3 py-2 border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>

                        {/* Password Input */}
                        <div>
                          <label htmlFor="login-password" className="block text-sm font-medium mb-2">
                            Password
                          </label>
                          <input
                            id="login-password"
                            name="password"
                            type="password"
                            autoComplete="current-password"
                            required
                            disabled={isRateLimited}
                            placeholder="Enter your password"
                            className="w-full px-3 py-2 border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>

                        {/* Submit Button */}
                        <button
                          type="submit"
                          disabled={isRateLimited}
                          className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${
                            isRateLimited
                              ? 'bg-muted text-muted-foreground cursor-not-allowed'
                              : 'bg-primary text-primary-foreground hover:bg-primary/90'
                          }`}
                        >
                          {isRateLimited ? 'Account Locked' : 'Sign In'}
                        </button>

                        {/* Google OAuth */}
                        <div className="mt-4">
                          <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                              <div className="w-full border-t border-border" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                              <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={async () => {
                              if (isRateLimited) {
                                toast({
                                  variant: "destructive",
                                  title: "Account Locked",
                                  description: "Please wait before trying again.",
                                });
                                return;
                              }

                              const { error } = await supabase.auth.signInWithOAuth({
                                provider: 'google',
                                options: {
                                  redirectTo: `${window.location.origin}/`,
                                },
                              });

                              if (error) {
                                toast({
                                  variant: "destructive",
                                  title: "OAuth Failed",
                                  description: error.message,
                                });
                              }
                            }}
                            disabled={isRateLimited}
                            className={`w-full mt-4 py-2 px-4 border rounded-md font-medium transition-colors flex items-center justify-center gap-2 ${
                              isRateLimited
                                ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                                : 'border-border bg-background text-foreground hover:bg-muted'
                            }`}
                          >
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            Sign in with Google
                          </button>
                        </div>
                      </form>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
            </main>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  return <>{children}</>;
}
